import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const AlertExclamationIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const JackpotWarningBanner = ({
  requiredCount = 7,
  missingUnits = [],
  onViewPreviousInvestments,
  previousQuarter = "Q2",
  upcomingQuarter = "Q3",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const missingCount = missingUnits.length;
  const formattedMissing = missingUnits
    .map((u) => (typeof u === "string" ? u : (u.unitCode || u.buUnit)))
    .filter(Boolean);

  const descriptionText = missingCount > 0
    ? `Based on ${previousQuarter} investments, you have invested in ${requiredCount} units. You currently have ${missingCount} unit${missingCount > 1 ? "s" : ""} (${formattedMissing.join(", ")}) not selected for ${upcomingQuarter}. Please ensure you invest in all of them again in ${upcomingQuarter} to remain eligible for the jackpot.`
    : `Based on ${previousQuarter} investments, you have invested in ${requiredCount} units. Please ensure you invest in all of them again in ${upcomingQuarter} to remain eligible for the jackpot.`;

  return (
    <Box
      sx={{
        backgroundColor: isDark ? "rgba(245, 158, 11, 0.07)" : "rgba(245, 158, 11, 0.09)",
        border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.28)" : "rgba(245, 158, 11, 0.35)"}`,
        borderRadius: "10px",
        px: { xs: 2, sm: 2.5 },
        py: 1.75,
        mb: 2.5,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: isDark ? "rgba(245, 158, 11, 0.15)" : "rgba(245, 158, 11, 0.14)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          <AlertExclamationIcon />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.92rem",
              color: isDark ? "#FBBF24" : "#92400E",
              letterSpacing: "-0.01em",
              mb: 0.25,
            }}
          >
            Jackpot Eligibility Check
          </Typography>
          <Typography
            sx={{
              fontSize: "0.82rem",
              color: isDark ? "rgba(255, 255, 255, 0.75)" : "#78350F",
              lineHeight: 1.45,
            }}
          >
            {descriptionText}
          </Typography>
        </Box>
      </Box>

      {onViewPreviousInvestments && (
        <Button
          onClick={onViewPreviousInvestments}
          variant="outlined"
          endIcon={<ExternalLinkIcon />}
          sx={{
            flexShrink: 0,
            borderColor: isDark ? "rgba(245, 158, 11, 0.35)" : "rgba(245, 158, 11, 0.45)",
            color: isDark ? "#FDE68A" : "#92400E",
            fontSize: "0.8rem",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "7px",
            px: 2,
            py: 0.75,
            backgroundColor: isDark ? "rgba(245, 158, 11, 0.05)" : "rgba(245, 158, 11, 0.08)",
            "&:hover": {
              borderColor: isDark ? "rgba(245, 158, 11, 0.6)" : "rgba(245, 158, 11, 0.7)",
              backgroundColor: isDark ? "rgba(245, 158, 11, 0.12)" : "rgba(245, 158, 11, 0.15)",
              color: isDark ? "#FFFFFF" : "#78350F",
            },
          }}
        >
          View Previous Investments
        </Button>
      )}
    </Box>
  );
};

export default JackpotWarningBanner;

